/* eslint-disable no-restricted-syntax */
import '../Table.scss';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';

import { getBaseScenario } from 'redux/PlannerModeSlice';
import { STATUS } from 'projectConstants';
import DeleteGroupModal from 'components/DeleteGroupModal';

import useBuilderPropertyMetadataSource from './useBuilderPropertyMetadataSource';
import usePropertyTableRowsFromMetadata from './usePropertyTableRowsFromMetadata';
import usePropertyTableColumnsLayout from './usePropertyTableColumnsLayout';
import usePropertyTableInteractionController from './usePropertyTableInteractionController';
import PropertyTableLoadingState from './PropertyTableLoadingState';
import PropertyTableReactGrid from './PropertyTableReactGrid';
import { applySavingOverlayToPropertyTableRows } from './buildPropertyTableGridRows';

const BuilderModePropertyTableMetadataView = ({
  planPermission,
  useV1Checkbox = false,
  hideTableAddRow = false,
}: {
  planPermission?: boolean;
  useV1Checkbox?: boolean;
  hideTableAddRow?: boolean;
}) => {
  const { blockId } = useParams();
  const baseScenario = useSelector(getBaseScenario);
  const scenarioId = baseScenario?.id;

  const { blockMetadata, blockMetadataStatus, metadataIndicators, refetch } =
    useBuilderPropertyMetadataSource(blockId, scenarioId);

  const { tableData, rows: baseRows } = usePropertyTableRowsFromMetadata(
    blockMetadata,
    metadataIndicators,
    blockId,
    scenarioId,
    { includeFooterRow: !hideTableAddRow },
  );

  const { setHostRef, columns, onColumnResized } = usePropertyTableColumnsLayout();

  const planPermissionReadOnly = Boolean(planPermission);

  const {
    gridProperties,
    deleteModal,
    setDeleteModal,
    customCellTemplates,
    savingPropertyCell,
    handleFocusLocationChanged,
    handleCellChange,
    handleRowsReorder,
    handleContextMenu,
    handleLocationChanged,
    handleDeleteConfirm,
  } = usePropertyTableInteractionController({
    blockId,
    refetchMetadata: refetch,
    tableData,
    rows: baseRows,
    planPermissionReadOnly,
    useV1Checkbox,
  });

  const rows = useMemo(
    () => applySavingOverlayToPropertyTableRows(baseRows, savingPropertyCell) ?? baseRows,
    [baseRows, savingPropertyCell],
  );

  const showLoading =
    blockMetadataStatus === STATUS.LOADING && (!blockMetadata || metadataIndicators.length === 0);

  const assignHostReference = useCallback(
    (node: HTMLDivElement | null) => {
      setHostRef(node);
    },
    [setHostRef],
  );

  return (
    <Box
      ref={assignHostReference}
      height={'100%'}
      width={'100%'}
      display={'flex'}
      flexDirection={'column'}
      flex={1}
      overflow={'hidden'}
    >
      {showLoading ? <PropertyTableLoadingState /> : null}
      {!showLoading && !!columns?.length && !!rows?.length ? (
        <PropertyTableReactGrid
          rows={rows}
          columns={columns}
          gridProperties={gridProperties}
          customCellTemplates={customCellTemplates}
          onRowsReordered={handleRowsReorder}
          onFocusLocationChanging={handleLocationChanged}
          onFocusLocationChanged={handleFocusLocationChanged}
          onCellsChanged={handleCellChange}
          onContextMenu={planPermission ? undefined : handleContextMenu}
          onColumnResized={planPermission ? undefined : onColumnResized}
          stickyBottomRows={hideTableAddRow ? 0 : undefined}
        />
      ) : null}
      <DeleteGroupModal
        isOpen={deleteModal.status}
        onClose={() => setDeleteModal({ indicatorId: '', indicatorName: '', status: false })}
        itemName={deleteModal.indicatorName || 'some name'}
        onDelete={handleDeleteConfirm}
        orgChart={true}
      />
    </Box>
  );
};

export default BuilderModePropertyTableMetadataView;
